import { IShopifyConfigurationResponseItem } from 'api/endpoints/meta/meta.response.interface';
import * as MetaShopifyTypes from './metaShoppify.types';
import { IInitialMetaState, TShopifyConnectionItem } from 'interfaces/meta.interface';
import { IShopifyQueryItem, IShopifyTemplateItem } from 'interfaces/superAdmin.interface';

export type TMetaShopifyDispatchReducer =
  | {
      type: typeof MetaShopifyTypes.DELETE_SHOPIFY_CONNECTION;
      payload: string;
    }
  | {
      type: typeof MetaShopifyTypes.SET_IS_LOADING_DELETE_SHOPIFY_CONNECTION;
      payload: {
        loading: boolean;
        id: string | null;
      };
    }
  | {
      type: typeof MetaShopifyTypes.ADD_NEW_SHOPIFY_CONNECTION;
      payload: TShopifyConnectionItem;
    }
  | {
      type: typeof MetaShopifyTypes.SET_IS_LOADING_CREATE_SHOPIFY_CONNECTIONS;
      payload: boolean;
    }
  | {
      type: typeof MetaShopifyTypes.UPDATE_SHOPIFY_CONNECTION;
      payload: {
        id: string;
        data: Partial<TShopifyConnectionItem>;
      };
    }
  | {
      type: typeof MetaShopifyTypes.SET_IS_LOADING_UPDATE_SHOPIFY_CONNECTIONS;
      payload: boolean;
    }
  | {
      type: typeof MetaShopifyTypes.SET_CONFIGURATION;
      payload: Array<IShopifyConfigurationResponseItem>;
    }
  | {
      type: typeof MetaShopifyTypes.SET_IS_LOADING_CONFIGURATION;
      payload: boolean;
    }
  | {
      type: typeof MetaShopifyTypes.CREATE_UPDATE_CONFIGURATION;
      payload: Array<IShopifyConfigurationResponseItem>;
    }
  | {
      type: typeof MetaShopifyTypes.SET_IS_LOADING_CREATE_UPDATE_CONFIGURATION;
      payload: boolean;
    }
  | {
      type: typeof MetaShopifyTypes.SET_QUERIES;
      payload: Array<IShopifyQueryItem>;
    }
  | {
      type: typeof MetaShopifyTypes.SET_IS_LOADING_QUERIES;
      payload: boolean;
    }
  | {
      type: typeof MetaShopifyTypes.SET_QUERY_SELECTED;
      payload: IShopifyQueryItem;
    }
  | {
      type: typeof MetaShopifyTypes.DELETE_QUERY;
      payload: string;
    }
  | {
      type: typeof MetaShopifyTypes.SET_IS_LOADING_DELETE_QUERY;
      payload: {
        loading: boolean;
        queryId: string | null;
      };
    }
  | {
      type: typeof MetaShopifyTypes.UPDATE_QUERY;
      payload: {
        id: string;
        data: Partial<IShopifyQueryItem>;
      };
    }
  | {
      type: typeof MetaShopifyTypes.SET_IS_LOADING_UPDATE_QUERY;
      payload: boolean;
    }
  | {
      type: typeof MetaShopifyTypes.SET_TEMPLATES;
      payload: Array<IShopifyTemplateItem>;
    }
  | {
      type: typeof MetaShopifyTypes.SET_IS_LOADING_TEMPLATES;
      payload: boolean;
    }
  | {
      type: typeof MetaShopifyTypes.SET_TEMPLATE_SELECTED;
      payload: IShopifyTemplateItem;
    }
  | {
      type: typeof MetaShopifyTypes.DELETE_TEMPLATE;
      payload: string;
    }
  | {
      type: typeof MetaShopifyTypes.SET_IS_LOADING_DELETE_TEMPLATE;
      payload: {
        loading: boolean;
        tag: string | null;
      };
    };

export function handleShopify(
  state: IInitialMetaState,
  action: TMetaShopifyDispatchReducer
) {
  const { type, payload } = action;

  switch (type) {
    case MetaShopifyTypes.ADD_NEW_SHOPIFY_CONNECTION: {
      const addNewShopifyConnection: IInitialMetaState = {
        ...state,
        shopify: {
          ...state.shopify,
          connections: [payload, ...state.shopify.connections],
          connectionSelected: payload,
        },
      };
      return addNewShopifyConnection;
    }

    case MetaShopifyTypes.UPDATE_SHOPIFY_CONNECTION: {
      const updateShopifyConnection: IInitialMetaState = {
        ...state,
        shopify: {
          ...state.shopify,
          connections: state.shopify.connections.map((item) =>
            item.id === payload.id ? { ...item, ...payload.data } : item
          ),
          connectionSelected: {
            ...(state.shopify.connectionSelected as TShopifyConnectionItem),
            ...payload.data,
          },
        },
      };
      return updateShopifyConnection;
    }

    case MetaShopifyTypes.SET_IS_LOADING_UPDATE_SHOPIFY_CONNECTIONS: {
      const setIsLoadingUpdateShopifyConnection: IInitialMetaState = {
        ...state,
        shopify: {
          ...state.shopify,
          isLoadingUpdateShopifyConnection: payload,
        },
      };
      return setIsLoadingUpdateShopifyConnection;
    }

    case MetaShopifyTypes.DELETE_SHOPIFY_CONNECTION: {
      const deleteShopifyConnection: IInitialMetaState = {
        ...state,
        shopify: {
          ...state.shopify,
          connections: state.shopify.connections.filter((item) => item.id !== payload),
        },
      };
      return deleteShopifyConnection;
    }

    case MetaShopifyTypes.SET_IS_LOADING_DELETE_SHOPIFY_CONNECTION: {
      const setIsLoadingRemoveShopifyConnection: IInitialMetaState = {
        ...state,
        shopify: {
          ...state.shopify,
          isLoadingRemoveShopifyConnection: {
            loading: payload.loading,
            connection_id: payload.id,
          },
        },
      };
      return setIsLoadingRemoveShopifyConnection;
    }

    // Configuration
    case MetaShopifyTypes.SET_CONFIGURATION: {
      const setConfiguration: IInitialMetaState = {
        ...state,
        shopify: {
          ...state.shopify,
          configuration: payload,
        },
      };
      return setConfiguration;
    }

    case MetaShopifyTypes.SET_IS_LOADING_CONFIGURATION: {
      const setIsLoadingConfiguration: IInitialMetaState = {
        ...state,
        shopify: {
          ...state.shopify,
          isLoadingConfiguration: payload,
        },
      };
      return setIsLoadingConfiguration;
    }

    case MetaShopifyTypes.CREATE_UPDATE_CONFIGURATION: {
      const createUpdateConfiguration: IInitialMetaState = {
        ...state,
        shopify: {
          ...state.shopify,
          configuration: payload,
        },
      };
      return createUpdateConfiguration;
    }

    case MetaShopifyTypes.SET_IS_LOADING_CREATE_UPDATE_CONFIGURATION: {
      const setIsLoadingCreateUpdateConfiguration: IInitialMetaState = {
        ...state,
        shopify: {
          ...state.shopify,
          isLoadingCreateUpdateConfiguration: payload,
        },
      };
      return setIsLoadingCreateUpdateConfiguration;
    }

    // Queries ///////////////////////////////////////////////////////
    case MetaShopifyTypes.SET_QUERIES: {
      const setQueries: IInitialMetaState = {
        ...state,
        shopify: {
          ...state.shopify,
          queries: payload,
        },
      };
      return setQueries;
    }
    case MetaShopifyTypes.SET_IS_LOADING_QUERIES: {
      const setIsLoadingQueries: IInitialMetaState = {
        ...state,
        shopify: {
          ...state.shopify,
          isLoadingQueries: payload,
        },
      };
      return setIsLoadingQueries;
    }
    case MetaShopifyTypes.SET_QUERY_SELECTED: {
      const setQuerySelected: IInitialMetaState = {
        ...state,
        shopify: {
          ...state.shopify,
          querySelected: payload,
        },
      };
      return setQuerySelected;
    }
    case MetaShopifyTypes.DELETE_QUERY: {
      const deleteQuery: IInitialMetaState = {
        ...state,
        shopify: {
          ...state.shopify,
          queries: state.shopify.queries.filter((item) => item.id !== payload),
        },
      };
      return deleteQuery;
    }
    case MetaShopifyTypes.SET_IS_LOADING_DELETE_QUERY: {
      const setIsLoadingDeleteQuery: IInitialMetaState = {
        ...state,
        shopify: {
          ...state.shopify,
          isLoadingDeleteQuery: {
            loading: payload.loading,
            queryId: payload.queryId,
          },
        },
      };
      return setIsLoadingDeleteQuery;
    }
    case MetaShopifyTypes.UPDATE_QUERY: {
      const updateQuery: IInitialMetaState = {
        ...state,
        shopify: {
          ...state.shopify,
          queries: state.shopify.queries.map((item) =>
            item.id === payload.id ? { ...item, ...payload.data } : item
          ),
        },
      };
      return updateQuery;
    }
    case MetaShopifyTypes.SET_IS_LOADING_UPDATE_QUERY: {
      const setIsLoadingUpdateQuery: IInitialMetaState = {
        ...state,
        shopify: {
          ...state.shopify,
          isLoadingUpdateQuery: payload,
        },
      };
      return setIsLoadingUpdateQuery;
    }

    // Templates /////////////////////////////////////////////////////
    case MetaShopifyTypes.SET_TEMPLATES: {
      const setTemplates: IInitialMetaState = {
        ...state,
        shopify: {
          ...state.shopify,
          templates: payload,
        },
      };
      return setTemplates;
    }

    case MetaShopifyTypes.SET_IS_LOADING_TEMPLATES: {
      const setIsLoadingTemplates: IInitialMetaState = {
        ...state,
        shopify: {
          ...state.shopify,
          isLoadingTemplates: payload,
        },
      };
      return setIsLoadingTemplates;
    }

    case MetaShopifyTypes.SET_TEMPLATE_SELECTED: {
      const setTemplateSelected: IInitialMetaState = {
        ...state,
        shopify: {
          ...state.shopify,
          templateSelected: payload,
        },
      };
      return setTemplateSelected;
    }

    case MetaShopifyTypes.DELETE_TEMPLATE: {
      const deleteTemplate: IInitialMetaState = {
        ...state,
        shopify: {
          ...state.shopify,
          templates: state.shopify.templates.filter((item) => item.tag !== payload),
        },
      };
      return deleteTemplate;
    }

    case MetaShopifyTypes.SET_IS_LOADING_DELETE_TEMPLATE: {
      const setIsLoadingDeleteTemplate: IInitialMetaState = {
        ...state,
        shopify: {
          ...state.shopify,
          isLoadingDeleteTemplate: {
            loading: payload.loading,
            templateTag: payload.tag,
          },
        },
      };
      return setIsLoadingDeleteTemplate;
    }

    default:
      return state;
  }
}
